import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { Sidebar } from "./components/Sidebar";
import { OrgThemeBridge } from "./components/OrgThemeBridge";
import { MapsRoute } from "./routes/Maps";
import { AcceptInviteRoute } from "./routes/AcceptInvite";
import { OrgMembersRoute, TeamMembersRoute } from "./routes/MembersPage";
import { OrgAdminRoute } from "./routes/OrgAdmin";

// Code-based router (dmarc-monitor pattern). Root renders the sidebar and a
// full-width scrollable main — page containers must NOT set max-width.

function RootLayout() {
  // Edição de mapa ocupa a tela inteira: escondemos a sidebar em /maps/:uuid
  // (o editor tem botão "Voltar" próprio no header, então não prende o usuário).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const fullscreen = pathname.startsWith("/maps/");
  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950">
      <OrgThemeBridge />
      {!fullscreen && <Sidebar />}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
});

const mapsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: MapsRoute,
});

// React Flow + d3-hierarchy são as deps mais pesadas do bundle e só quem abre
// um mapa precisa delas — lazy para ficarem fora do chunk inicial.
const mapEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/maps/$uuid",
  component: lazyRouteComponent(() => import("./routes/MapEditor"), "MapEditorRoute"),
});

// Aceite de convite — renderiza como overlay (fixed) por cima do layout.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: AcceptInviteRoute,
});

const orgAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId",
  component: OrgAdminRoute,
});
const orgMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orgs/$orgId/members",
  component: OrgMembersRoute,
});
const teamMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/$teamId/members",
  component: TeamMembersRoute,
});

const routeTree = rootRoute.addChildren([
  mapsRoute,
  mapEditorRoute,
  inviteRoute,
  orgAdminRoute,
  orgMembersRoute,
  teamMembersRoute,
]);

export const router = createRouter({ routeTree });

// ---- Router público (anônimo) ---------------------------------------------
// Shell mínimo, SEM sidebar e SEM gate de login. Só a rota /s/:token, que
// resolve o link no worker (GET /api/shared/:token). Isolado do router
// autenticado para nunca expor navegação/recursos ao anônimo.
const publicRootRoute = createRootRoute({
  component: () => (
    <div className="h-screen bg-slate-50 dark:bg-slate-950">
      <Outlet />
    </div>
  ),
});

const publicMapRoute = createRoute({
  getParentRoute: () => publicRootRoute,
  path: "/s/$token",
  component: lazyRouteComponent(() => import("./routes/MapEditor"), "PublicMapRoute"),
});

const publicRouteTree = publicRootRoute.addChildren([publicMapRoute]);
export const publicRouter = createRouter({ routeTree: publicRouteTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
